import { createSlice } from "@reduxjs/toolkit";
import type { Subscription, SubscriptionPlan } from "../../interfaces/SubscriptionObject";
import { resetAllState } from "./resetAllState";
import {
  cancelSubscription,
  fetchSubscriptionPlans,
  fetchUserSubscriptions,
  subscribeBusiness,
} from "../../services/thunks/SubscriptionThunk";

interface SubscriptionsState {
  /** All of the current user's subscriptions, across every business they own. */
  subscriptions: Subscription[];
  /** Same rows keyed by businessId, for O(1) lookup from a business-scoped view. */
  subscriptionByBusiness: Record<string, Subscription>;
  /** The catalog of plans available to subscribe to (small/medium/large/etc.)
   *  — not business-scoped, so no by-id map alongside it like subscriptions has. */
  plans: SubscriptionPlan[];
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const initialState: SubscriptionsState = {
  subscriptions: [],
  subscriptionByBusiness: {},
  plans: [],
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

export type SubscriptionOp = "subscribeBusiness" | "cancelSubscription";

/** Per-business loading/error/success key, shared by this slice's own
 *  extraReducers and by useSubscribeBusiness/useCancelSubscription — a single
 *  source for the key format so the three places can't drift apart. */
export function subscriptionOpKey(op: SubscriptionOp, businessId: string): string {
  return `${op}_${businessId}`;
}

/** Replace the row for this businessId, or append if it's new — keeps the
 *  list and the by-business map in sync without requiring a refetch. */
function upsertSubscription(state: SubscriptionsState, sub: Subscription): void {
  state.subscriptionByBusiness[sub.businessId] = sub;
  const idx = state.subscriptions.findIndex((s) => s.businessId === sub.businessId);
  if (idx === -1) {
    state.subscriptions.push(sub);
  } else {
    state.subscriptions[idx] = sub;
  }
}

const subscriptionsSlice = createSlice({
  name: "subscriptions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ── fetch (all subscriptions for the current user) ──
      .addCase(fetchUserSubscriptions.pending, (state) => {
        state.loadingMap.fetchUserSubscriptions = "Loading subscriptions...";
        state.errorMap.fetchUserSubscriptions = null;
        state.successMap.fetchUserSubscriptions = false;
      })
      .addCase(fetchUserSubscriptions.fulfilled, (state, action) => {
        state.loadingMap.fetchUserSubscriptions = null;
        state.successMap.fetchUserSubscriptions = true;
        state.subscriptions = action.payload;
        state.subscriptionByBusiness = {};
        for (const sub of action.payload) {
          state.subscriptionByBusiness[sub.businessId] = sub;
        }
      })
      .addCase(fetchUserSubscriptions.rejected, (state, action) => {
        state.loadingMap.fetchUserSubscriptions = null;
        state.errorMap.fetchUserSubscriptions = action.payload as string;
      })
      // ── subscribe (per-business, so one business's button doesn't disable
      //    another's while its own request is in flight) ──
      .addCase(subscribeBusiness.pending, (state, action) => {
        const key = subscriptionOpKey("subscribeBusiness", action.meta.arg.businessId);
        state.loadingMap[key] = "Setting up auto-pay...";
        state.errorMap[key] = null;
        state.successMap[key] = false;
      })
      .addCase(subscribeBusiness.fulfilled, (state, action) => {
        const key = subscriptionOpKey("subscribeBusiness", action.meta.arg.businessId);
        state.loadingMap[key] = null;
        state.successMap[key] = true;
        upsertSubscription(state, action.payload);
      })
      .addCase(subscribeBusiness.rejected, (state, action) => {
        const key = subscriptionOpKey("subscribeBusiness", action.meta.arg.businessId);
        state.loadingMap[key] = null;
        state.errorMap[key] = action.payload as string;
      })
      // ── cancel (per-business, same rationale as subscribe) ──
      .addCase(cancelSubscription.pending, (state, action) => {
        const key = subscriptionOpKey("cancelSubscription", action.meta.arg.businessId);
        state.loadingMap[key] = "Cancelling subscription...";
        state.errorMap[key] = null;
        state.successMap[key] = false;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        const key = subscriptionOpKey("cancelSubscription", action.meta.arg.businessId);
        state.loadingMap[key] = null;
        state.successMap[key] = true;
        upsertSubscription(state, action.payload);
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        const key = subscriptionOpKey("cancelSubscription", action.meta.arg.businessId);
        state.loadingMap[key] = null;
        state.errorMap[key] = action.payload as string;
      })
      // ── fetch (subscription plans catalog) — global, same key shape as
      //    fetchUserSubscriptions since plans aren't business-scoped ──
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.loadingMap.fetchSubscriptionPlans = "Loading plans...";
        state.errorMap.fetchSubscriptionPlans = null;
        state.successMap.fetchSubscriptionPlans = false;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.loadingMap.fetchSubscriptionPlans = null;
        state.successMap.fetchSubscriptionPlans = true;
        state.plans = action.payload;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.loadingMap.fetchSubscriptionPlans = null;
        state.errorMap.fetchSubscriptionPlans = action.payload as string;
      })
      .addCase(resetAllState, () => initialState);
  },
});

export const selectSubscriptions = (state: any): Subscription[] =>
  state.subscriptions.subscriptions;

export const selectSubscriptionForBusiness =
  (businessId: string | null | undefined) =>
  (state: any): Subscription | undefined =>
    businessId ? state.subscriptions.subscriptionByBusiness[businessId] : undefined;

export const selectSubscriptionPlans = (state: any): SubscriptionPlan[] =>
  state.subscriptions.plans;

export const selectSubscriptionsLoadingMap = (state: any) => state.subscriptions.loadingMap;
export const selectSubscriptionsErrorMap = (state: any) => state.subscriptions.errorMap;
export const selectSubscriptionsSuccessMap = (state: any) => state.subscriptions.successMap;

export default subscriptionsSlice.reducer;
